import { useCallback, useState, useMemo } from 'react';
import { useAsyncEffect } from 'react-utils/hooks/useAsyncEffect';
import { fetchCrmSearch } from '../api/crmSearchApi';
import { registerQuery, useQuery } from 'data-fetching-client';
import { events } from '../usage-tracker/events';
import { CommerceTool } from '../types/CommerceTool';
import { track } from '../usage-tracker/track';
import { fetchHubSetting, saveHubSetting } from '../api/hubSettingsApi';
import { fetchDoesPortalHaveUSCompanyAddress } from '../api/companyDetailsApi';
import Raven from 'raven-js';
import { useHasAllScopes } from 'growth-payments-core/auth/scopes';
import { useHubUserAttribute } from 'growth-payments-core/frontend-preferences/hooks';
import { HAS_DISMISSED_COMMERCE_SPECIALISTS_CUSTOM_RATE_ALERT } from '../constants/frontendPreferenceKeys';
import { shouldShowZeroState, useKYCStatus } from 'growth-payments-core/eligibility/utils';
const MIN_TOOL_INSTANCES_INCLUSIVE = 1;
const MAX_TOOL_INSTANCES_EXCLUSIVE = 6;
const TOOL_COUNT_QUERY = registerQuery({
  fieldName: 'toolCount',
  args: ['query'],
  fetcher: async ({
    query
  }) => {
    const {
      total
    } = await fetchCrmSearch(query);
    return total;
  }
});
function mapCommerceToolToObjectType(toolName) {
  switch (toolName) {
    case CommerceTool.INVOICES:
      return 'INVOICE';
    case CommerceTool.PAYMENT_LINKS:
      return 'PAYMENT_LINK';
    case CommerceTool.SUBSCRIPTIONS:
      return 'SUBSCRIPTION';
    default:
      throw new Error(`Unexpected CommerceTool provided: ${toolName}`);
  }
}
function getFilterGroups(toolName) {
  switch (toolName) {
    case CommerceTool.INVOICES:
      return [{
        filters: [{
          property: 'hs_invoice_status',
          operator: 'NEQ',
          value: 'draft'
        }]
      }];
    default:
      return [];
  }
}
const useToolCount = (toolName, skip) => {
  var _totalToolCountData$t, _finalizedToolCountDa;
  const totalToolCountQuery = useMemo(() => {
    return {
      objectTypeId: mapCommerceToolToObjectType(toolName)
    };
  }, [toolName]);
  const finalizedToolCountQuery = useMemo(() => {
    return {
      objectTypeId: mapCommerceToolToObjectType(toolName),
      filterGroups: getFilterGroups(toolName)
    };
  }, [toolName]);
  const {
    loading: totalToolCountLoading,
    data: totalToolCountData
  } = useQuery(TOOL_COUNT_QUERY, {
    variables: {
      query: totalToolCountQuery
    },
    skip
  });
  const {
    loading: finalizedToolCountLoading,
    data: finalizedToolCountData
  } = useQuery(TOOL_COUNT_QUERY, {
    variables: {
      query: finalizedToolCountQuery
    },
    skip
  });
  const loading = totalToolCountLoading || finalizedToolCountLoading;
  return {
    toolCountLoading: loading,
    totalToolCount: (_totalToolCountData$t = totalToolCountData === null || totalToolCountData === void 0 ? void 0 : totalToolCountData.toolCount) !== null && _totalToolCountData$t !== void 0 ? _totalToolCountData$t : 0,
    finalizedToolCount: (_finalizedToolCountDa = finalizedToolCountData === null || finalizedToolCountData === void 0 ? void 0 : finalizedToolCountData.toolCount) !== null && _finalizedToolCountDa !== void 0 ? _finalizedToolCountDa : 0
  };
};
const useModal = toolName => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = source => {
    track(events.commerceSpecialistsInteraction, {
      action: 'commerceSpecialistsContactAlertInteraction',
      subAction: 'openContactModal',
      app: toolName,
      source
    });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
  };
  return {
    modalOpen,
    openModal,
    closeModal
  };
};
const usePortalIsInUS = hasCommerceSpecialistsScope => {
  const [portalIsInUS, setPortalIsInUS] = useState(false);
  const [portalIsInUSLoading, setPortalIsInUSLoading] = useState(hasCommerceSpecialistsScope);
  useAsyncEffect(async () => {
    if (hasCommerceSpecialistsScope) {
      try {
        const isInUS = await fetchDoesPortalHaveUSCompanyAddress();
        setPortalIsInUS(isInUS);
      } catch (e) {
        Raven.captureException(e);
      } finally {
        setPortalIsInUSLoading(false);
      }
    }
  }, [hasCommerceSpecialistsScope]);
  return [portalIsInUSLoading, portalIsInUS];
};
const useIneligibleForSpecialistsContact = ({
  toolName,
  hasCommerceSpecialistsScope = true
}) => {
  const [openModalLinkAvailable, setOpenModalLinkAvailable] = useState(false);
  const [ineligibleForSpecialistsContact, setIneligibleForSpecialistsContact] = useState(false);
  const [userPreferenceLoading, setUserPreferenceLoading] = useState(false);
  const hubSettingKey = `CommerceGrowth:IneligibleForSpecialistsContact:${toolName}`;
  useAsyncEffect(async () => {
    if (hasCommerceSpecialistsScope) {
      setUserPreferenceLoading(true);
      const value = await fetchHubSetting(hubSettingKey, 'false');
      setIneligibleForSpecialistsContact(value === 'true');
      setUserPreferenceLoading(false);
    }
  }, [hubSettingKey, hasCommerceSpecialistsScope]);
  const skipFetchFinalizedToolCount = !hasCommerceSpecialistsScope || ineligibleForSpecialistsContact;
  const {
    toolCountLoading,
    totalToolCount,
    finalizedToolCount
  } = useToolCount(toolName, skipFetchFinalizedToolCount);
  const loading = userPreferenceLoading || toolCountLoading;
  useAsyncEffect(async () => {
    if (!loading && !ineligibleForSpecialistsContact) {
      if (finalizedToolCount >= MAX_TOOL_INSTANCES_EXCLUSIVE) {
        await saveHubSetting(hubSettingKey, 'true');
      } else if (totalToolCount >= MIN_TOOL_INSTANCES_INCLUSIVE) {
        setOpenModalLinkAvailable(true);
      }
    }
  }, [loading, ineligibleForSpecialistsContact, totalToolCount, finalizedToolCount, hubSettingKey]);
  return [loading, ineligibleForSpecialistsContact, openModalLinkAvailable];
};
const useCommerceSpecialistsContactAvailability = (toolName, _hasAllGates, hasAllScopes) => {
  const hasCommerceSpecialistsScope = hasAllScopes('commerce-specialists-growth-contact-access');
  const [ineligibleForSpecialistsContactLoading, ineligibleForSpecialistsContact, openModalLinkAvailable] = useIneligibleForSpecialistsContact({
    toolName,
    hasCommerceSpecialistsScope
  });
  const [portalIsInUSLoading, portalIsInUS] = usePortalIsInUS(hasCommerceSpecialistsScope);
  const available = !ineligibleForSpecialistsContact && hasCommerceSpecialistsScope && portalIsInUS;
  const loading = ineligibleForSpecialistsContactLoading || portalIsInUSLoading;
  const showOpenModalLink = openModalLinkAvailable && available;
  return [loading, available, showOpenModalLink];
};
const valueProcessor = expectedValue => value => expectedValue === value;
export const useCommerceSpecialistsContact = (toolName, hasAllGates, hasAllScopes) => {
  const [availableLoading, available, showOpenModalLink] = useCommerceSpecialistsContactAvailability(toolName, hasAllGates, hasAllScopes);
  const hubUserAttributeKey = `CommerceGrowth:HasViewedSpecialistsContactModal:${toolName}`;
  const skipHubUserAttributeFetch = availableLoading || !available;
  const [hasViewedSpecialistsContactModal, updateHasViewedSpecialistsContactModal] = useHubUserAttribute(hubUserAttributeKey, 'false', valueProcessor('true'), skipHubUserAttributeFetch);
  const loading = availableLoading || !skipHubUserAttributeFetch && hasViewedSpecialistsContactModal === null;
  const showModalOnEditorAbandon = !hasViewedSpecialistsContactModal && available;
  const {
    modalOpen,
    openModal,
    closeModal
  } = useModal(toolName);
  return {
    loading,
    showOpenModalLink,
    showModalOnEditorAbandon,
    modalOpen,
    openModal: (source, setViewedModalPreference) => {
      openModal(source);
      if (setViewedModalPreference) {
        updateHasViewedSpecialistsContactModal('true');
      }
    },
    closeModal
  };
};
export const useCommerceHomeSpecialistsContact = (toolName, hasAllGates, hasAllScopes) => {
  const hasCommerceSpecialistsScope = hasAllScopes('commerce-specialists-growth-contact-access');
  const {
    modalOpen,
    openModal,
    closeModal
  } = useModal(toolName);
  const [loading, eligible] = usePortalIsInUS(hasCommerceSpecialistsScope);
  return {
    loading,
    eligible,
    modalOpen,
    openModal,
    closeModal
  };
};
export const useCommerceSpecialistsCustomRateAlert = () => {
  const hasAllScopes = useHasAllScopes();
  const hasCommerceSpecialistsScope = hasAllScopes('commerce-specialists-growth-contact-access');
  const [isPortalStateLoading, isPortalStateEligible] = usePortalIsInUS(hasCommerceSpecialistsScope);
  const kycStatus = useKYCStatus();
  const isPreEnroll = kycStatus === undefined ? false : shouldShowZeroState(kycStatus);
  const [hasDismissedCustomRateAlert, setHasDismissedCustomRateAlert] = useHubUserAttribute(HAS_DISMISSED_COMMERCE_SPECIALISTS_CUSTOM_RATE_ALERT, 'false', valueProcessor('true'));
  const dismissCustomRateAlert = useCallback(() => {
    setHasDismissedCustomRateAlert('true');
  }, [setHasDismissedCustomRateAlert]);
  const loading = isPortalStateLoading || kycStatus === undefined;
  const eligible = isPreEnroll && isPortalStateEligible && hasDismissedCustomRateAlert === false; // explicitly check for `false` since the value will be `null` while loading

  return {
    loading,
    eligible,
    dismissCustomRateAlert
  };
};